import { useSession } from '@clerk/clerk-react';
import { GetToken } from '@clerk/types';
import to from 'await-to-js';
import axios from 'axios';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { environmentContextState } from '../recoil/atoms';

declare const window: HoneyloveWindow;

const defaultHeadersFn = (token: string) => ({ withCredentials: true, headers: { Authorization: token } });

export interface HoneyloveApi {
  apiGet: <T>(url: string) => Promise<T | undefined>;
  apiDelete: <T>(url: string) => Promise<T | undefined>;
  apiPost: <T>(url: string, payload: any) => Promise<T | undefined>;
  apiPut: <T>(url: string, payload: any) => Promise<T | undefined>;
  apiPatch: <T>(url: string, payload: any) => Promise<T | undefined>;
}

const authedGet = async <T>(url: string, getToken: GetToken): Promise<T | undefined> => {
  const token = `Bearer ${await getToken()}`;
  const headers = defaultHeadersFn(token);
  const [err, response] = await to(axios.get<T>(url, headers));

  if (err) {
    window.Sentry.captureException(err);
  }

  const isSuccess = response?.status && [200, 201, 418].includes(response.status);
  if (!isSuccess) {
    const error: Error = { name: `${response?.data ? response?.status : 'No data'}`, message: 'Error' };
    window.Sentry.captureException(error);
    throw error;
  }

  return response?.data;
};

const authedDelete = async <T>(url: string, getToken: GetToken): Promise<T | undefined> => {
  const token = `Bearer ${await getToken()}`;
  const headers = defaultHeadersFn(token);
  const [err, response] = await to(axios.delete<T>(url, headers));

  if (err) {
    window.Sentry.captureException(err);
  }
  return response?.data;
};

const authedPost = async <T>(url: string, payload: any, getToken: GetToken): Promise<T | undefined> => {
  const token = `Bearer ${await getToken()}`;
  const headers = defaultHeadersFn(token);
  const [err, response] = await to(axios.post<T>(url, payload, headers));

  if (err) {
    window.Sentry.captureException(err);
  }
  return response?.data;
};

const authedPatch = async <T>(url: string, payload: any, getToken: GetToken): Promise<T | undefined> => {
  const token = `Bearer ${await getToken()}`;
  const headers = defaultHeadersFn(token);
  const [err, response] = await to(axios.patch<T>(url, payload, headers));

  if (err) {
    window.Sentry.captureException(err);
  }
  return response?.data;
};

const authedPut = async <T>(url: string, payload: any, getToken: GetToken): Promise<T | undefined> => {
  const token = `Bearer ${await getToken()}`;
  const headers = defaultHeadersFn(token);
  const [err, response] = await to(axios.put<T>(url, payload, headers));

  if (err) {
    window.Sentry.captureException(err);
  }
  return response?.data;
};
const addDomainFn = (baseUrl: string) => (path: string) => path.startsWith(baseUrl) ? path : `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
export const useHoneyloveAuthedApi = (): HoneyloveApi => {
  const { session } = useSession();
  const getTokenFallback: GetToken = async () => null;
  const getToken = session?.getToken ?? getTokenFallback;
  const environment = useRecoilValue(environmentContextState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const addDomain = useCallback(addDomainFn(`https://${environment.dataApiDomain}`), [environment.dataApiDomain]);
  const getFn = <T>(path: string) => authedGet<T>(addDomain(path), getToken);
  const deleteFn = <T>(path: string) => authedDelete<T>(addDomain(path), getToken);
  const postFn = <T>(path: string, payload: any) => authedPost<T>(addDomain(path), payload, getToken);
  const putFn = <T>(path: string, payload: any) => authedPut<T>(addDomain(path), payload, getToken);
  const patchFn = <T>(path: string, payload: any) => authedPatch<T>(addDomain(path), payload, getToken);
  const apiGet = useCallback(getFn, [addDomain, getToken]);
  const apiDelete = useCallback(deleteFn, [addDomain, getToken]);
  const apiPost = useCallback(postFn, [addDomain, getToken]);
  const apiPut = useCallback(putFn, [addDomain, getToken]);
  const apiPatch = useCallback(patchFn, [addDomain, getToken]);
  return { apiGet, apiDelete, apiPost, apiPut, apiPatch };
};
